import { Injectable } from '@angular/core';
import { AppwriteDatabaseService, ConfirmationDialogResponse, ConfirmationDialogService } from '@prism/angular-shared';
import { Collection, defaultMoldCheckupSettings, GlobalVariableDocumentIds,
    MOLD_CHECKUP_SETTINGS_KIND, moldCheckupTransitionArms, normalizeMoldCheckupSettings, MoldCheckupTaskUpdate, PlasticsMold, PlasticsMoldCheckup, PlasticsMoldCheckupCommand,
    PlasticsMoldCheckupCommandResult, PlasticsMoldCheckupSettings, PlasticsMoldNotice, PlasticsMoldingTask,
    ProductVersion, ProductVersionStatus, RotoSchedule } from '@prism/prism-shared';
import { Query } from 'appwrite';
import { catchError, defer, first, firstValueFrom, forkJoin, from, map, merge, Observable, of, Subject, switchMap, tap, throwError } from 'rxjs';
import { createMoldCheckupRuntime, executeMoldCheckupCommand } from './mold-checkup-command';

type CommandInput = PlasticsMoldCheckupCommand extends infer C ? C extends { requestId: string } ? Omit<C, 'requestId'> : never : never;

@Injectable({ providedIn: 'root' })
export class MoldCheckupApiService {
    private pendingIds = new Map<string, string>();
    private pendingTransitions = new Map<string, CommandInput>();
    private committedCheckups$ = new Subject<void>();
    constructor(private db: AppwriteDatabaseService, private confirmation: ConfirmationDialogService) { }

    settings(): Observable<PlasticsMoldCheckupSettings> {
        return this.db.list<PlasticsMoldCheckupSettings>(Collection.globalVariables, [Query.equal('kind', MOLD_CHECKUP_SETTINGS_KIND), Query.limit(2)]).pipe(
            map(response => {
                if (response.items.length > 1) throw new Error('Multiple mold checkup settings records need review.');
                return normalizeMoldCheckupSettings(response.items[0] ?? defaultMoldCheckupSettings());
            }));
    }
    openCheckups(): Observable<PlasticsMoldCheckup[]> {
        return merge(this.db.collection<PlasticsMoldCheckup>(Collection.plasticsMoldCheckup, ref => ref.where('status', 'in', ['Queued', 'In progress'])).valueChanges(),
            this.committedCheckups$).pipe(
            switchMap(() => this.db.listAll<PlasticsMoldCheckup>(Collection.plasticsMoldCheckup, [Query.equal('status', ['Queued', 'In progress'])])));
    }
    settingsChanges(): Observable<PlasticsMoldCheckupSettings> {
        return this.db.collection<PlasticsMoldCheckupSettings>(Collection.globalVariables, ref => ref.where('kind', '==', MOLD_CHECKUP_SETTINGS_KIND).limit(2)).valueChanges().pipe(
            map(values => {
                if (values.length > 1) throw new Error('Multiple mold checkup settings records need review.');
                return normalizeMoldCheckupSettings(values[0] ?? defaultMoldCheckupSettings());
            }));
    }
    checkup(id: string): Observable<PlasticsMoldCheckup> { return this.db.get(Collection.plasticsMoldCheckup, id); }
    maintenanceMolds(): Observable<PlasticsMold[]> { return this.db.listAll<PlasticsMold>(Collection.plasticsMold); }
    maintenanceProducts(moldFamilyId: string): Observable<ProductVersion[]> {
        return this.db.listAll<ProductVersion>(Collection.productVersion, [Query.equal('moldFamilyId', moldFamilyId), Query.equal('status', ProductVersionStatus.Released)]).pipe(
            map(values => {
                const latest = new Map<string, ProductVersion>();
                for (const value of [...values].sort((a, b) => (b.versionNumber ?? 0) - (a.versionNumber ?? 0))) {
                    if (!latest.has(value.productId)) latest.set(value.productId, value);
                }
                return [...latest.values()].sort((a, b) => a.partNumber.localeCompare(b.partNumber));
            }));
    }
    productImage(productId: string): Observable<string> {
        if (!productId) return of('');
        return this.db.listAll<ProductVersion>(Collection.productVersion,
            [Query.equal('productId', productId), Query.equal('status', ProductVersionStatus.Released)]).pipe(
            map(versions => [...versions].sort((a, b) => (b.versionNumber ?? 0) - (a.versionNumber ?? 0))
                .map(version => version.imageUrl || version.thumbnailImageUrl).find(Boolean) || ''));
    }
    history(moldId: string): Observable<PlasticsMoldCheckup[]> {
        return this.db.listAll<PlasticsMoldCheckup>(Collection.plasticsMoldCheckup, [Query.equal('moldId', moldId), Query.orderDesc('createdOn')]);
    }
    notices(moldId: string): Observable<PlasticsMoldNotice[]> {
        return this.db.listAll<PlasticsMoldNotice>(Collection.plasticsMoldNotice, [Query.equal('moldId', moldId), Query.orderDesc('createdOn')]);
    }
    command(input: CommandInput): Observable<PlasticsMoldCheckupCommandResult> {
        return defer(() => {
            // Retain the ID through a lost response and a browser refresh. Never retain a badge.
            const safe: any = { ...input }; delete safe.supervisorBadgeId;
            const key = `mold-checkup-request:${JSON.stringify(safe)}`;
            let requestId: string | null = null;
            try { requestId = sessionStorage.getItem(key); } catch { /* Storage can be disabled on a kiosk. */ }
            requestId ||= this.pendingIds.get(key) || this.db.createId();
            this.pendingIds.set(key, requestId);
            try { sessionStorage.setItem(key, requestId); } catch { /* In-memory request still works. */ }
            const clear = () => { this.pendingIds.delete(key); try { sessionStorage.removeItem(key); } catch { /* Optional persistence. */ } };
            return from(executeMoldCheckupCommand({ ...input, requestId }, createMoldCheckupRuntime(this.db))).pipe(tap(result => {
                clear();
                // Transactions must refresh this browser even if the realtime event is delayed or unavailable.
                if (result.checkupIds.length) this.committedCheckups$.next();
            }), catchError(error => {
                if ([400, 401, 403, 404, 409].includes(error.statusCode ?? error.code)) clear();
                return throwError(() => error);
            }));
        });
    }

    /** Administrative edits preserve placement; physical changeovers confirm it before one atomic save. */
    transition(schedule: RotoSchedule, updates: PlasticsMoldingTask[] = [], options: { scheduleOnly?: boolean; machineChangeover?: boolean; supervisorBadgeId?: string } = {}): Observable<void> {
        const key = `mold-checkup-transition:${JSON.stringify({ scheduleOnly: !!options.scheduleOnly, machineChangeover: !!options.machineChangeover,
            lists: schedule.productLists, updates: updates.map(task => ({ id: task.id, status: task.status,
            moldId: task.moldId, totalQuantity: task.totalQuantity, note: task.note, kitNeeded: task.kitNeeded, hotListOrderItemId: task.hotListOrderItemId })) })}`;
        return defer(() => {
            let pending = this.pendingTransitions.get(key);
            try { pending ||= JSON.parse(sessionStorage.getItem(key) || 'null') || undefined; } catch { /* Optional persistence. */ }
            // Replay the original confirmed command before inspecting the now-advanced schedule.
            if (pending) return this.executeTransition({ ...pending, supervisorBadgeId: options.supervisorBadgeId } as CommandInput, key);
            return forkJoin({
                settings: this.settings(),
                current: this.db.get<RotoSchedule>(Collection.globalVariables, GlobalVariableDocumentIds.RotoSchedule),
                molds: this.db.listAll<PlasticsMold>(Collection.plasticsMold),
            }).pipe(
                switchMap(({ settings, current, molds }) => {
                    if (schedule.expectedLastSavedOn && new Date(schedule.expectedLastSavedOn).getTime() !== new Date(current.lastSavedOn).getTime()) {
                        return throwError(() => new Error('The roto schedule changed. Refresh before saving your change.'));
                    }
                    // During staged rollout, existing operation remains available until setup is applied.
                    if (!settings.id) {
                        const { expectedLastSavedOn, ...legacySchedule } = schedule;
                        return this.db.batchSetAcrossCollectionsTransactional({
                            [Collection.plasticsMoldingTask]: updates,
                            [Collection.globalVariables]: [{ ...legacySchedule, id: GlobalVariableDocumentIds.RotoSchedule } as any],
                        });
                    }
                    return from(this.confirmTransition(current, schedule, molds, updates, options, key));
                }),
            );
        });
    }
    private executeTransition(input: CommandInput, key: string): Observable<void> {
        const safe: any = { ...input }; delete safe.supervisorBadgeId;
        this.pendingTransitions.set(key, safe);
        try { sessionStorage.setItem(key, JSON.stringify(safe)); } catch { /* Optional persistence. */ }
        const clear = () => { this.pendingTransitions.delete(key); try { sessionStorage.removeItem(key); } catch { /* Optional persistence. */ } };
        return this.command(input).pipe(tap(clear), map(() => void 0), catchError(error => {
            if ([400, 401, 403, 404, 409].includes(error.statusCode ?? error.code)) clear();
            return throwError(() => error);
        }));
    }

    private async ask(mold: PlasticsMold, canContinue: boolean): Promise<ConfirmationDialogResponse> {
        // Subscribe before displaying; teardown completes/replaces the response subject.
        const response = firstValueFrom(this.confirmation.response$.pipe(first()));
        this.confirmation.displayDialog(`Mold ${mold.serialNumber ?? ''} ${mold.name}`,
            canContinue
                ? 'Is this mold physically coming off? Choose remove if it comes off, continue if the same mold stays mounted for the next compatible task, or cancel.'
                : 'Confirm that this mold is physically coming off the machine. Cancel if it is still mounted.',
            canContinue ? [ConfirmationDialogResponse.REMOVE, ConfirmationDialogResponse.CONTINUE, ConfirmationDialogResponse.CANCEL]
                : [ConfirmationDialogResponse.REMOVE, ConfirmationDialogResponse.CANCEL]);
        const answer = await response;
        this.confirmation.destroyDialog();
        return answer;
    }
    private async confirmTransition(current: RotoSchedule, schedule: RotoSchedule, molds: PlasticsMold[], updates: PlasticsMoldingTask[],
        options: { scheduleOnly?: boolean; machineChangeover?: boolean; supervisorBadgeId?: string }, key: string): Promise<void> {
        const next = structuredClone(schedule);
        const removedMoldIds: string[] = [], continuations: Array<{ moldId: string; taskId: string }> = [];
        const taskUpdates = new Map<string, MoldCheckupTaskUpdate>(updates.map(task => [task.id,
            { id: task.id, status: task.status, moldId: task.moldId || '', completedOn: task.completedOn,
                totalQuantity: task.totalQuantity, note: task.note, kitNeeded: task.kitNeeded, hotListOrderItemId: task.hotListOrderItemId }]));
        if (options.scheduleOnly) {
            await firstValueFrom(this.executeTransition({ type: 'transition', scheduleOnly: true, expectedSchedule: current, schedule: next,
                taskUpdates: [...taskUpdates.values()], removedMoldIds, continuations }, key));
            return;
        }
        const ids = [...new Set([...current.productLists, ...next.productLists].flatMap(list => list.moldingTaskIds)
            .concat(molds.flatMap(mold => mold.placement ? [mold.placement.taskId] : [])))];
        const tasks = await firstValueFrom(this.db.listByIds<PlasticsMoldingTask>(Collection.plasticsMoldingTask, ids));
        const tasksById = new Map(tasks.map(task => [task.id, task]));
        updates.forEach(task => tasksById.set(task.id, task));
        const affectedArms = moldCheckupTransitionArms(current, next, updates.map(task => task.id));
        // Production-manager completion can target a mounted task already removed from the administrative queue.
        if (!options.machineChangeover) for (const mold of molds) {
            if (mold.placement && updates.some(task => task.id === mold.placement!.taskId)) {
                affectedArms.add(`${mold.placement.machineId}/${mold.placement.armId}`);
            }
        }
        const versions = await firstValueFrom(this.db.listByFieldValues<ProductVersion>(Collection.productVersion, 'productId',
            [...new Set(tasks.map(task => task.productId).filter(Boolean))]));
        for (const mold of molds.filter(item => item.placement && affectedArms.has(`${item.placement.machineId}/${item.placement.armId}`))) {
            const placement = mold.placement!;
            const nextList = next.productLists.find(list => list.id === placement.productListId
                && list.machineId === placement.machineId && list.armId === placement.armId);
            const nextTask = tasksById.get(nextList?.moldingTaskIds[0] || '');
            if (nextTask?.id === placement.taskId && nextTask.moldId === mold.id
                && nextList?.machineId === placement.machineId && nextList.armId === placement.armId) continue;
            const compatible = !!nextTask && !!nextList && nextList.machineId === placement.machineId && nextList.armId === placement.armId
                && versions.some(version => version.status === ProductVersionStatus.Released && version.productId === nextTask.productId && version.moldFamilyId === mold.moldFamilyId)
                && (!nextTask.moldId || nextTask.moldId === mold.id);
            // Saving the supervisor changeover confirms the setup. Retain only the selected physical mold;
            // otherwise remove it and show the persisted destination notice on the machine page.
            const answer = options.machineChangeover
                ? compatible && nextTask?.moldId === mold.id ? ConfirmationDialogResponse.CONTINUE : ConfirmationDialogResponse.REMOVE
                : await this.ask(mold, compatible);
            if (answer === ConfirmationDialogResponse.CANCEL) throw new Error('Mold change canceled. Refresh the schedule before making another change.');
            if (answer === ConfirmationDialogResponse.CONTINUE && nextTask) {
                continuations.push({ moldId: mold.id, taskId: nextTask.id });
                taskUpdates.set(nextTask.id, { ...taskUpdates.get(nextTask.id), id: nextTask.id, status: nextTask.status, moldId: mold.id, completedOn: nextTask.completedOn });
            } else {
                removedMoldIds.push(mold.id);
                // A moved task retains a historical moldId unless explicitly cleared.
                for (const list of next.productLists) {
                    const task = tasksById.get(list.moldingTaskIds[0]);
                    if (task?.moldId === mold.id && !options.machineChangeover) {
                        taskUpdates.set(task.id, { ...taskUpdates.get(task.id), id: task.id, status: task.status, moldId: '', completedOn: task.completedOn });
                    }
                }
            }
        }
        await firstValueFrom(this.executeTransition({ type: 'transition', expectedSchedule: current, schedule: next,
            taskUpdates: [...taskUpdates.values()], removedMoldIds, continuations, supervisorBadgeId: options.supervisorBadgeId }, key));
    }
}
